import React, { useEffect, useMemo, useState } from "react";
import api, { formatIDR } from "../lib/api";
import Layout from "../components/Layout";
import { Check, X, Search, Wallet, Landmark, PiggyBank, Undo2, Image as ImageIcon, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

const STATUS = {
  pending: { label: "Menunggu", cls: "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/40" },
  approved: { label: "Disetujui", cls: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/30" },
  paid: { label: "Dibayar", cls: "bg-[#059669]/10 text-[#059669] border-[#059669]/30" },
  rejected: { label: "Ditolak", cls: "bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/30" },
};
const TABS = [["", "Semua"], ["pending", "Menunggu"], ["approved", "Disetujui"], ["paid", "Dibayar"], ["rejected", "Ditolak"]];

const TeamReimburse = () => {
  const [data, setData] = useState({ items: [], counts: {}, transfer_pending: 0 });
  const [month, setMonth] = useState(thisMonth());
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [payModal, setPayModal] = useState(null); // {items}
  const [payForm, setPayForm] = useState({});
  const [editPay, setEditPay] = useState(null); // payment doc
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data: d } = await api.get("/team-reimbursements", { params: month ? { month } : {} });
    setData(d);
    window.dispatchEvent(new Event("team-reimb-changed"));
    setSelected((s) => s.filter((id) => d.items.some((i) => i.id === id && i.team_status === "approved" && !i.team_payment_id)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const filtered = useMemo(() => {
    let rows = data.items;
    if (tab) rows = rows.filter((r) => r.team_status === tab);
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter((r) => [r.name, r.nik, r.order_no, r.customer_name, r.customer_city, r.category, r.remark].some((v) => (v || "").toLowerCase().includes(s)));
    }
    return rows;
  }, [data.items, tab, search]);

  const sum = (st) => data.items.filter((r) => r.team_status === st).reduce((a, r) => a + Number(r.amount || 0), 0);
  const payable = (r) => r.team_status === "approved" && !r.team_payment_id;
  const err = (e, d) => toast.error(e?.response?.data?.detail || d);

  const approve = async (r) => {
    try { await api.post(`/team-reimbursements/${r.id}/approve`); toast.success("Disetujui"); load(); } catch (e) { err(e, "Gagal menyetujui"); }
  };
  const unapprove = async (r) => {
    try { await api.post(`/team-reimbursements/${r.id}/unapprove`); toast.success("Persetujuan dibatalkan"); load(); } catch (e) { err(e, "Gagal"); }
  };
  const doReject = async () => {
    setBusy(true);
    try {
      await api.post(`/team-reimbursements/${rejectFor.id}/reject`, { reason: rejectReason });
      toast.success("Pengajuan ditolak"); setRejectFor(null); setRejectReason(""); load();
    } catch (e) { err(e, "Gagal menolak"); } finally { setBusy(false); }
  };

  const toggle = (r) => {
    if (!payable(r)) return;
    setSelected((s) => {
      if (s.includes(r.id)) return s.filter((x) => x !== r.id);
      const first = data.items.find((i) => i.id === s[0]);
      if (first && first.nik !== r.nik) { toast.error("Satu pembayaran hanya untuk satu teknisi"); return s; }
      return [...s, r.id];
    });
  };

  const openPay = (items) => {
    const total = items.reduce((a, r) => a + Number(r.amount || 0), 0);
    setPayForm({ method: "petty_cash", pay_date: today(), amount: total, transfer_status: "belum", note: "" });
    setPayModal({ items, total });
  };
  const submitPay = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      const body = { ids: payModal.items.map((r) => r.id), method: payForm.method, pay_date: payForm.pay_date, note: payForm.note,
        amount: payForm.method === "transfer" ? Number(payForm.amount) : null, transfer_status: payForm.transfer_status };
      const { data: res } = await api.post("/team-reimbursements/pay", body);
      const p = res.payment;
      if (p.method === "petty_cash") toast.success("Dibayar dari Petty Cash — tercatat di Petty Cash & Pengeluaran, pesan terkirim ke teknisi");
      else {
        if (p.owner_wa?.ok) toast.success("Pembayaran transfer dibuat — info WA masuk antrian kirim ke " + p.owner_wa.phone);
        else toast.warning("Pembayaran transfer dibuat, tapi WA gagal: " + (p.owner_wa?.error || "-"));
        if (p.transfer_status === "sudah") toast.success("Pesan karyawan terkirim ke teknisi (48 jam)");
      }
      if (res.alert?.low) toast.warning(res.alert.sent ? "Saldo petty cash di bawah batas — info WA terkirim" : "Saldo petty cash di bawah batas");
      setPayModal(null); setSelected([]); load();
    } catch (e) { err(e, "Gagal menyimpan pembayaran"); } finally { setBusy(false); }
  };

  const openEditPay = (p) => setEditPay({ ...p });
  const saveEditPay = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      const { data: p } = await api.put(`/team-reimbursements/payments/${editPay.id}`, {
        amount: Number(editPay.amount), pay_date: editPay.pay_date, transfer_status: editPay.transfer_status, note: editPay.note || "" });
      toast.success(p.transfer_status === "sudah" ? "Transfer selesai — pesan karyawan terkirim ke teknisi (48 jam)" : "Pembayaran disimpan");
      setEditPay(null); load();
    } catch (e) { err(e, "Gagal menyimpan"); } finally { setBusy(false); }
  };
  const cancelPay = async () => {
    if (!window.confirm("Batalkan pembayaran transfer ini? Pengajuan kembali ke status Disetujui.")) return;
    try { await api.delete(`/team-reimbursements/payments/${editPay.id}`); toast.success("Pembayaran dibatalkan"); setEditPay(null); load(); } catch (e) { err(e, "Gagal"); }
  };

  const selItems = data.items.filter((r) => selected.includes(r.id));
  const editItems = editPay ? data.items.filter((r) => (editPay.item_ids || []).includes(r.id)) : [];

  return (
    <Layout>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-cap mb-2">Material & Lainnya (per Order)</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Reimbursement Tim</h1>
        </div>
        <button disabled={!selItems.length} onClick={() => openPay(selItems)} data-testid="treimb-pay-selected"
          className="bg-[#002FA7] hover:bg-[#00227A] disabled:opacity-40 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
          <Wallet size={14} /> Bayar Terpilih{selItems.length ? ` (${selItems.length} · ${formatIDR(selItems.reduce((a, r) => a + Number(r.amount || 0), 0))})` : ""}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[["pending", "Menunggu Persetujuan", "text-[#B45309]"], ["approved", "Disetujui (belum dibayar)", "text-[#002FA7]"], ["paid", "Sudah Dibayar", "text-[#059669]"], ["rejected", "Ditolak", "text-[#DC2626]"]].map(([k, l, c]) => (
          <button key={k} onClick={() => setTab(k)} className={`text-left bg-white border p-4 ${tab === k ? "border-[#111113]" : "border-[#E4E4E7]"}`}>
            <div className={`label-cap ${c}`}>{l}</div>
            <div className="font-mono font-bold text-xl mt-1">{data.counts[k] || 0} <span className="text-sm text-[#52525B]">· {formatIDR(sum(k))}</span></div>
            {k === "approved" && data.transfer_pending > 0 && <div className="text-[10px] text-[#B45309] mt-1">{data.transfer_pending} transfer belum dilakukan</div>}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E4E4E7] p-4 mb-4 flex items-end gap-3 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`treimb-tab-${k || "all"}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase border ${tab === k ? "bg-[#111113] text-white border-[#111113]" : "border-[#E4E4E7] bg-white"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[200px] px-2">
          <Search size={14} className="text-[#52525B]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari teknisi/pelanggan/kota/order…" className="flex-1 outline-none text-sm bg-transparent" data-testid="treimb-search" />
        </div>
        <div>
          <label className="label-cap block mb-1">Bulan Transaksi</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-2 py-1.5 border border-[#E4E4E7] text-xs font-mono bg-white" data-testid="treimb-month" />
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] overflow-x-auto">
        <table className="w-full min-w-[980px]">
          <thead>
            <tr className="border-b border-[#E4E4E7]">
              <th className="px-3 py-3 w-8"></th>
              <th className="text-left label-cap px-3 py-3">Tanggal</th>
              <th className="text-left label-cap px-3 py-3">Teknisi</th>
              <th className="text-left label-cap px-3 py-3">Order · Pelanggan · Kota</th>
              <th className="text-left label-cap px-3 py-3">Kategori</th>
              <th className="text-left label-cap px-3 py-3">Keterangan</th>
              <th className="text-left label-cap px-3 py-3">Struk</th>
              <th className="text-right label-cap px-3 py-3">Jumlah</th>
              <th className="text-left label-cap px-3 py-3">Status</th>
              <th className="text-right label-cap px-3 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="10" className="p-8 text-center text-sm text-[#52525B]">Tidak ada pengajuan.</td></tr>}
            {filtered.map((r) => {
              const st = STATUS[r.team_status] || STATUS.pending;
              const p = r.payment;
              return (
                <tr key={r.id} className="border-b border-[#E4E4E7] row-hover align-top" data-testid={`treimb-row-${r.id}`}>
                  <td className="px-3 py-3">
                    {payable(r) && <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r)} className="accent-[#002FA7]" data-testid={`treimb-check-${r.id}`} />}
                  </td>
                  <td className="px-3 py-3 text-sm font-mono whitespace-nowrap">{r.date}</td>
                  <td className="px-3 py-3 text-sm font-bold">{r.name}<div className="text-[10px] font-mono text-[#52525B]">{r.nik}</div></td>
                  <td className="px-3 py-3 text-sm">
                    <div className="font-bold">{r.customer_name || "—"}{r.customer_city ? <span className="font-normal text-[#52525B]"> · {r.customer_city}</span> : null}</div>
                    <div className="text-[11px] font-mono text-[#52525B]">{r.order_no || "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-sm">{r.category}</td>
                  <td className="px-3 py-3 text-xs text-[#52525B] max-w-[220px]">
                    {r.remark || "—"}
                    {r.duplicate_suspect && <div className="text-[#B45309] mt-1">Mirip pengajuan lain: {r.duplicate_suspect.reason}</div>}
                    {r.receipt_date_unread && <div className="text-[#B45309] mt-1 font-bold" data-testid={`treimb-date-unread-${r.id}`}>⚠ {r.receipt_date_note || "Tanggal struk tidak terbaca"} — cek manual</div>}
                    {r.receipt_date && r.receipt_date !== r.date && <div className="text-[#52525B] mt-1">Tgl di struk: {r.receipt_date}</div>}
                    {r.team_status === "rejected" && r.reject_reason && <div className="text-[#DC2626] mt-1">{r.reject_reason}</div>}
                    {r.ocr && r.ocr.amount > 0 && Number(r.ocr.amount) !== Number(r.amount) && <div className="text-[#DC2626] mt-1">Struk terbaca {formatIDR(r.ocr.amount)}</div>}
                  </td>
                  <td className="px-3 py-3">
                    {r.photo_url ? (
                      <a href={r.photo_url} target="_blank" rel="noreferrer" className="inline-block">
                        {r.thumb_url ? <img src={r.thumb_url} alt="struk" className="w-12 h-12 object-cover border border-[#E4E4E7]" /> : <ImageIcon size={18} className="text-[#002FA7]" />}
                      </a>
                    ) : <span className="text-[10px] text-[#52525B]">tanpa foto</span>}
                  </td>
                  <td className="px-3 py-3 text-sm font-mono font-bold text-right whitespace-nowrap">{formatIDR(r.amount)}</td>
                  <td className="px-3 py-3 text-xs">
                    <span className={`text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 ${st.cls}`}>{st.label}</span>
                    {p && (
                      <div className="mt-1 text-[11px] text-[#52525B]">
                        {p.method === "petty_cash" ? "Petty Cash" : "Transfer"} · {p.pay_date}
                        {p.method === "transfer" && <div className={p.transfer_status === "sudah" ? "text-[#059669] font-bold" : "text-[#B45309] font-bold"}>{p.transfer_status === "sudah" ? "Sudah ditransfer" : "Belum ditransfer"}{p.amount != null ? ` · ${formatIDR(p.amount)}` : ""}</div>}
                        {p.message_id && <div className="flex items-center gap-1 text-[#059669]"><MessageSquare size={10} /> pesan terkirim</div>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex gap-1">
                      {r.team_status === "pending" && (
                        <>
                          <button onClick={() => approve(r)} title="Approve" className="p-2 hover:bg-[#059669]/10 text-[#059669] border border-[#E4E4E7]" data-testid={`treimb-approve-${r.id}`}><Check size={14} /></button>
                          <button onClick={() => { setRejectFor(r); setRejectReason(""); }} title="Reject" className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]" data-testid={`treimb-reject-${r.id}`}><X size={14} /></button>
                        </>
                      )}
                      {payable(r) && (
                        <>
                          <button onClick={() => openPay([r])} className="px-2 py-1.5 text-[10px] font-bold uppercase bg-[#002FA7] text-white" data-testid={`treimb-pay-${r.id}`}>Bayar</button>
                          <button onClick={() => unapprove(r)} title="Batalkan persetujuan" className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><Undo2 size={14} /></button>
                          <button onClick={() => { setRejectFor(r); setRejectReason(""); }} title="Reject" className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><X size={14} /></button>
                        </>
                      )}
                      {p && p.method === "transfer" && p.transfer_status !== "sudah" && (
                        <button onClick={() => openEditPay(p)} className="px-2 py-1.5 text-[10px] font-bold uppercase bg-[#F59E0B] text-white" data-testid={`treimb-transfer-${r.id}`}>Update Transfer</button>
                      )}
                      {p && p.method === "transfer" && p.transfer_status === "sudah" && (
                        <button onClick={() => openEditPay(p)} className="px-2 py-1.5 text-[10px] font-bold uppercase border border-[#E4E4E7]">Detail</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {payModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setPayModal(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitPay} className="bg-white border border-[#E4E4E7] w-full max-w-xl max-h-[92vh] overflow-y-auto" data-testid="treimb-pay-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <div className="label-cap">Pembayaran Reimbursement</div>
              <div className="font-heading text-lg font-bold">{payModal.items[0].name} · {formatIDR(payModal.total)}</div>
            </div>
            <div className="p-6 space-y-4">
              <div className="border border-[#E4E4E7] divide-y divide-[#E4E4E7] text-xs">
                {payModal.items.map((r) => (
                  <div key={r.id} className="px-3 py-2 flex justify-between gap-2">
                    <span>{r.date} · {r.category} · <b>{r.customer_name || "-"}</b>{r.customer_city ? ` (${r.customer_city})` : ""} · <span className="font-mono">{r.order_no}</span></span>
                    <span className="font-mono font-bold whitespace-nowrap">{formatIDR(r.amount)}</span>
                  </div>
                ))}
              </div>
              <div>
                <label className="label-cap block mb-1.5">Metode Pembayaran<span className="text-[#DC2626]">*</span></label>
                <div className="flex gap-2">
                  {[["petty_cash", "Dari Petty Cash", PiggyBank], ["transfer", "Transfer Bank", Landmark]].map(([k, l, Ic]) => (
                    <button type="button" key={k} onClick={() => setPayForm({ ...payForm, method: k })} data-testid={`treimb-method-${k}`}
                      className={`flex-1 px-3 py-2 text-xs font-bold uppercase border flex items-center justify-center gap-2 ${payForm.method === k ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#E4E4E7] bg-white"}`}><Ic size={14} /> {l}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-cap block mb-1.5">Tanggal Pembayaran<span className="text-[#DC2626]">*</span></label>
                  <input type="date" required value={payForm.pay_date} onChange={(e) => setPayForm({ ...payForm, pay_date: e.target.value })} className={ic + " font-mono"} data-testid="treimb-pay-date" />
                </div>
                {payForm.method === "transfer" && (
                  <div>
                    <label className="label-cap block mb-1.5">Jumlah Ditransfer (IDR)<span className="text-[#DC2626]">*</span></label>
                    <input type="number" required min="1" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className={ic + " font-mono"} data-testid="treimb-pay-amount" />
                  </div>
                )}
              </div>
              {payForm.method === "transfer" && (
                <div>
                  <label className="label-cap block mb-1.5">Status Transfer</label>
                  <div className="flex gap-2">
                    {[["belum", "Belum Ditransfer"], ["sudah", "Sudah Ditransfer"]].map(([k, l]) => (
                      <button type="button" key={k} onClick={() => setPayForm({ ...payForm, transfer_status: k })} data-testid={`treimb-status-${k}`}
                        className={`flex-1 px-3 py-2 text-xs font-bold uppercase border ${payForm.transfer_status === k ? "bg-[#111113] text-white border-[#111113]" : "border-[#E4E4E7] bg-white"}`}>{l}</button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="label-cap block mb-1.5">Catatan</label>
                <textarea rows={2} value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className={ic} />
              </div>
              <p className="text-[11px] text-[#52525B]">
                {payForm.method === "petty_cash"
                  ? "Otomatis tercatat sebagai Dana Keluar Petty Cash (dan Pengeluaran) per item, lalu pesan detail pembayaran langsung dikirim ke teknisi lewat Pesan Karyawan (tampil 48 jam)."
                  : "Info total reimbursement dikirim via WA ke 081315809797. Pesan detail pembayaran ke teknisi (48 jam) baru dikirim setelah status Sudah Ditransfer."}
              </p>
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setPayModal(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" disabled={busy} className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white hover:bg-[#00227A] disabled:opacity-50" data-testid="treimb-pay-submit">{busy ? "Menyimpan…" : "Submit"}</button>
            </div>
          </form>
        </div>
      )}

      {editPay && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setEditPay(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveEditPay} className="bg-white border border-[#E4E4E7] w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="treimb-transfer-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <div className="label-cap">Transfer Bank</div>
              <div className="font-heading text-lg font-bold">{editPay.name} · total {formatIDR(editPay.total)}</div>
            </div>
            <div className="p-6 space-y-4">
              <div className="border border-[#E4E4E7] divide-y divide-[#E4E4E7] text-xs">
                {editItems.map((r) => (
                  <div key={r.id} className="px-3 py-2 flex justify-between gap-2">
                    <span>{r.date} · {r.category} · <b>{r.customer_name || "-"}</b>{r.customer_city ? ` (${r.customer_city})` : ""}</span>
                    <span className="font-mono font-bold">{formatIDR(r.amount)}</span>
                  </div>
                ))}
              </div>
              {editPay.owner_wa && <p className={`text-[11px] ${editPay.owner_wa.ok ? "text-[#059669]" : "text-[#DC2626]"}`}>WA ke {editPay.owner_wa.phone}: {editPay.owner_wa.ok ? (editPay.owner_wa.queued ? "masuk antrian kirim" : "terkirim") : "gagal — " + (editPay.owner_wa.error || "")}</p>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-cap block mb-1.5">Jumlah Ditransfer</label>
                  <input type="number" min="1" required disabled={editPay.transfer_status === "sudah" && !!editPay.message_id} value={editPay.amount} onChange={(e) => setEditPay({ ...editPay, amount: e.target.value })} className={ic + " font-mono"} />
                </div>
                <div>
                  <label className="label-cap block mb-1.5">Tanggal Pembayaran</label>
                  <input type="date" required disabled={editPay.transfer_status === "sudah" && !!editPay.message_id} value={editPay.pay_date} onChange={(e) => setEditPay({ ...editPay, pay_date: e.target.value })} className={ic + " font-mono"} />
                </div>
              </div>
              <div>
                <label className="label-cap block mb-1.5">Status</label>
                <div className="flex gap-2">
                  {[["belum", "Belum Ditransfer"], ["sudah", "Sudah Ditransfer"]].map(([k, l]) => (
                    <button type="button" key={k} disabled={!!editPay.message_id} onClick={() => setEditPay({ ...editPay, transfer_status: k })} data-testid={`treimb-edit-status-${k}`}
                      className={`flex-1 px-3 py-2 text-xs font-bold uppercase border disabled:opacity-60 ${editPay.transfer_status === k ? "bg-[#111113] text-white border-[#111113]" : "border-[#E4E4E7] bg-white"}`}>{l}</button>
                  ))}
                </div>
                {editPay.message_id && <p className="text-[11px] text-[#059669] mt-1">Pesan pembayaran sudah dikirim ke teknisi.</p>}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-between gap-2">
              {editPay.transfer_status !== "sudah" || !editPay.message_id
                ? <button type="button" onClick={cancelPay} className="px-4 py-2 text-xs font-bold uppercase border border-[#DC2626] text-[#DC2626]">Batalkan Pembayaran</button>
                : <span />}
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditPay(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Tutup</button>
                {!editPay.message_id && <button type="submit" disabled={busy} className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white disabled:opacity-50" data-testid="treimb-transfer-submit">{busy ? "Menyimpan…" : "Submit"}</button>}
              </div>
            </div>
          </form>
        </div>
      )}

      {rejectFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setRejectFor(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">Tolak Reimbursement?</h3>
            <p className="text-sm text-[#52525B] mb-3">{rejectFor.name} · {rejectFor.date} · {formatIDR(rejectFor.amount)}</p>
            <textarea rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Alasan (tampil di aplikasi absensi teknisi)" className={ic} data-testid="treimb-reject-reason" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRejectFor(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={doReject} disabled={busy} className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white disabled:opacity-50" data-testid="treimb-confirm-reject">Tolak</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const ic = "w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white disabled:bg-[#F4F4F5]";

export default TeamReimburse;
